import {
  CreateParams,
  CreateResult,
  DataProvider,
  GetListResult,
  GetManyParams,
  GetManyResult,
  GetOneParams,
  GetOneResult,
  Identifier,
  UpdateParams,
  UpdateResult,
} from 'react-admin'
import { NOTIFICATION_CONDITIONAL_CONTENTS_URL } from '../../api-urls'
import { get, getWithPagination, patch, post } from '../../common/fetch.utils'
import {
  filterParamsComposer,
  queryParamsComposer,
} from '../../common/get-by-conditions.utils'
import { TicketAPIGetListParams } from '../../common/ticket-api-get-list.params'
import { mapSortConditionalContentsParam } from './notification-conditional-contents-sort.mapper'
import filterMapper from './notification-conditional-contents-filter.mapper'
import { NotificationConditionalContentDto } from '../../dto/system-notifications/notification-conditional-content.dto'

const provider = {
  getList: async (
    resource: string,
    { filter, sort, pagination }: TicketAPIGetListParams,
  ): Promise<GetListResult<NotificationConditionalContentDto>> => {
    const filterParams = `o=>${filterParamsComposer('o', filter, filterMapper)}`
    const pathParams = queryParamsComposer(
      sort,
      pagination,
      mapSortConditionalContentsParam,
    )
    const path = `/${filterParams}${(pathParams && `?${pathParams}`) || ''}`

    const {
      data,
      range: { total },
    } = await getWithPagination<NotificationConditionalContentDto[]>(
      `${NOTIFICATION_CONDITIONAL_CONTENTS_URL}/GetByConditions`,
      path,
    )

    return Promise.resolve({
      data,
      total,
    })
  },
  getOne: async (
    resource: string,
    { id }: GetOneParams,
  ): Promise<GetOneResult<NotificationConditionalContentDto>> => {
    const data = await get<NotificationConditionalContentDto>(
      NOTIFICATION_CONDITIONAL_CONTENTS_URL,
      `/${id}`,
    )
    return Promise.resolve({
      data,
    })
  },
  getMany: async (
    resource: string,
    { ids }: GetManyParams,
  ): Promise<GetManyResult<NotificationConditionalContentDto>> => {
    const data = await get<NotificationConditionalContentDto[]>(
      NOTIFICATION_CONDITIONAL_CONTENTS_URL,
      `/GetByConditions/e=>new int[] {${ids.toString()}}.Contains(e.Id)`,
    )
    return Promise.resolve({ data })
  },
  create: async (
    resource: string,
    { data }: CreateParams<CreateNotificationConditionalContentRequest>,
  ): Promise<CreateResult<NotificationConditionalContentDto>> => {
    const content = await post<
      CreateNotificationConditionalContentRequest[],
      NotificationConditionalContentDto[]
    >(NOTIFICATION_CONDITIONAL_CONTENTS_URL, [data])
    return {
      data: content[0],
    }
  },
  update: async (
    resource: string,
    { id, data, previousData }: UpdateParams<NotificationConditionalContentDto>,
  ): Promise<UpdateResult> => {
    const update = await patch<
      { id: Identifier; [n: string]: any },
      NotificationConditionalContentDto
    >(NOTIFICATION_CONDITIONAL_CONTENTS_URL, {
      ...Object.fromEntries(
        Object.entries(data)?.map(([key, value]) => {
          if (value !== previousData?.[key]) {
            return [key, value]
          }
          return [key, undefined]
        }),
      ),
      id,
    })
    return Promise.resolve({ data: update })
  },
} as DataProvider

export interface CreateNotificationConditionalContentRequest {
  readonly description: string
}

export default provider
